import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { combineLatest, from, Observable, of, timer } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { map, tap, concatMap, groupBy, mergeMap, toArray, share, shareReplay, catchError } from 'rxjs/operators';
import { Agent, AgentApiResponse } from '../../../domain/interfaces/agents';
import peelPage from '../../../core/pipes/peel-first-page.operator';
import { LoginService } from '@uoa/auth';
import { StatusSummaryComponent } from '../../wallboard/status-summary/status-summary.component';
import { CardFrameComponent } from '../../wallboard/card-frame/card-frame.component';
import { AgentCardComponent } from '../../wallboard/agent-card/agent-card.component';
import { AsyncPipe } from '@angular/common';
import { IonicSlides } from '@ionic/angular';

interface RequestOptions {
  headers?: {
    'x-api-key': string;
  };
}

type SwiperContainerElement = HTMLElement & {
  initialize: () => void;
  swiper?: {
    update: () => void;
    autoplay?: {
      start: () => void;
    };
  };
};

const sortOrder = (statusIdentifier: string) => {
  switch (statusIdentifier) {
    case 'inbound':
      return 1;
    case 'emails':
      return 2;
    case 'webchat':
      return 3;
    case 'auckland online':
      return 4;
    case 'outbound':
      return 5;
    case 'break':
      return 6;
    case 'team leader':
      return 7;
    default:
      // Z
      return 8;
  }
};

@Component({
  selector: 'app-agent-board',
  templateUrl: './agent-board.component.html',
  styleUrls: ['./agent-board.component.scss'],
  imports: [StatusSummaryComponent, CardFrameComponent, AgentCardComponent, AsyncPipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AgentBoardComponent implements OnInit {
  private httpClient = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private loginService = inject(LoginService);
  private swiperElement?: SwiperContainerElement;
  private swiperInitialized = false;

  @ViewChild('agentSwiper')
  set agentSwiper(elementRef: ElementRef<SwiperContainerElement> | undefined) {
    this.swiperElement = elementRef?.nativeElement;
    this.syncSwiper();
  }

  agents$!: Observable<Agent[][]>;
  agentPages$!: Observable<Agent[][][]>;
  swiperModules = [IonicSlides];
  swiperOptions = {
    modules: [IonicSlides],
    speed: 1000,
    allowTouchMove: false,
    effect: 'fade',
    autoplay: { delay: 20000 },
    fadeEffect: { crossFade: true },
  };

  available = 0;
  notAvailable = 0;
  outOfOffice = 0;

  televisionWidth = 1280;

  statusCompare(a: Agent[], b: Agent[]): number {
    return sortOrder(a[0].CurrentEvent.Status.Item.Identifier) - sortOrder(b[0].CurrentEvent.Status.Item.Identifier);
  }

  dateCompare(a: Agent, b: Agent): number {
    return new Date(a.CurrentEvent.Date).getTime() - new Date(b.CurrentEvent.Date).getTime();
  }

  ngOnInit() {
    this.route.data.subscribe((api) => {
      if (environment.wallboards.staff.agents.mockData) {
        const mockAgents = environment.wallboards.staff.agents.mockData as AgentApiResponse;
        this.agents$ = of(mockAgents).pipe(
          tap(this.tapAgents),
          map(this.filterAgents),
          mergeMap((agents: Agent[]) => from(agents)),
          groupBy((agent: Agent) => agent.CurrentEvent.Status.Item.GroupName),
          mergeMap((group) => group.pipe(toArray())),
          map((agentsInGroup: Agent[]) => agentsInGroup.sort(this.dateCompare)),
          toArray(),
          map((groupedAgents: Agent[][]) => groupedAgents.sort(this.statusCompare)),
          catchError(() => of([])),
        );

        this.setupAgentPages();
      } else {
        let options: RequestOptions = {};
        let urlSuffix = '';
        const apikey = this.route.snapshot.params['key'];
        if (apikey) {
          options = {
            headers: {
              'x-api-key': apikey,
            },
          };
          urlSuffix = '-tv';
        }
        // soft reload timer
        this.agents$ = timer(0, 8000).pipe(
          // workaround until auth library can handle `x-amzn-errortype: UnauthorizedException` header
          tap(async () => {
            if (!apikey) {
              await this.loginService.doLogin(this.router.url);
            }
          }),
          concatMap(() =>
            this.httpClient.get<AgentApiResponse>(api.baseUrl + urlSuffix, options).pipe(
              // agents
              tap(this.tapAgents),
              map(this.filterAgents),
              // split array into separate emitted objects
              mergeMap((s: Agent[]) => from(s)),
              // group objects together by identifier
              groupBy((s: Agent) => s.CurrentEvent.Status.Item.GroupName),
              // convert each group to arrays
              mergeMap((group) => group.pipe(toArray())),
              // sort within each group: date descending
              //tap(c => console.info(c)),
              map((agentsInGroup: Agent[]) => agentsInGroup.sort(this.dateCompare)),
              // do the opposite of the split above, and emit a single array (of arrays)
              toArray(),
              // sort status groups
              map((groupedAgents: Agent[][]) => groupedAgents.sort(this.statusCompare)),

              catchError(() => of([])),
            ),
          ),
        );

        this.agents$.subscribe((agentGroups) => console.log(agentGroups));

        // avoid doing http call multiple times
        this.setupAgentPages();

        this.scheduleHardReload();
      }
    });
  }

  protected scheduleHardReload() {
    window.setTimeout(
      () => {
        window.location.reload();
      },
      1000 * 60 * 60,
    );
  }

  filterAgents = (response: AgentApiResponse) =>
    response.data.Items.filter(
      (agent) =>
        agent.CurrentEvent?.Status?.Item?.Visible &&
        agent.CurrentEvent?.Status?.Item?.Visible !== 'false' &&
        agent.FullAgentName &&
        !agent.FullAgentName.includes('IAM'),
    );

  tapAgents = (response: AgentApiResponse) => {
    console.log('Updating board using', response.data?.Items);
    this.available = response.data?.Items.filter(
      (item) =>
        item.CurrentEvent?.Status?.Item?.Colour.toLowerCase() == 'green' &&
        (item.CurrentEvent?.Status?.Item?.Visible === true || item.CurrentEvent?.Status?.Item?.Visible === 'true'),
    ).length;
    this.notAvailable = response.data?.Items.filter(
      (item) =>
        item.CurrentEvent?.Status?.Item?.Colour.toLowerCase() == 'orange' &&
        (item.CurrentEvent?.Status?.Item?.Visible === true || item.CurrentEvent?.Status?.Item?.Visible === 'true'),
    ).length;
    this.outOfOffice = response.data?.Items.filter(
      (item) =>
        item.CurrentEvent?.Status?.Item?.Colour.toLowerCase() == 'red' &&
        (item.CurrentEvent?.Status?.Item?.Visible === true || item.CurrentEvent?.Status?.Item?.Visible === 'true'),
    ).length;
  };

  setupAgentPages = () => {
    const sharedAgentGroups = this.agents$.pipe(share());

    this.agentPages$ = combineLatest([
      sharedAgentGroups.pipe((source) => peelPage(source, 1)),
      sharedAgentGroups.pipe((source) => peelPage(source, 2)),
      sharedAgentGroups.pipe((source) => peelPage(source, 3)),
      sharedAgentGroups.pipe((source) => peelPage(source, 4)),
    ]).pipe(
      map((pages) => pages.filter((page) => page.length > 0)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.agentPages$.subscribe(() => {
      queueMicrotask(() => this.syncSwiper());
    });
  };

  private syncSwiper() {
    if (!this.swiperElement) {
      return;
    }

    if (!this.swiperInitialized) {
      if (typeof this.swiperElement.initialize === 'function') {
        Object.assign(this.swiperElement, this.swiperOptions);
        this.swiperElement.initialize();
        this.swiperElement.swiper?.autoplay?.start();
        this.swiperInitialized = true;
      } else {
        return;
      }
    }

    this.swiperElement.swiper?.update();
  }
}
